import {
  BaseballLineupPosition,
  Dexterity,
  IPlaceholderPlayerConfig,
  PlayerSearchService,
  PlayerSelector,
} from '@trackman/baseball-shared-components';
import { ReactComponent as DragDropIcon } from 'components/icons/dragIcon.svg';
import { ReactComponent as SuccessBrandIcon } from 'components/icons/successBrand.svg';
import { ServiceTypes } from 'constants/ServiceTypes';
import { HandednessFields } from 'enums/HandednessFields';
import PlayerType from 'enums/PlayerType';
import { StrikeZoneDefinitionEnum } from 'interfaces/session/IStrikeZoneDefinition';
import { ILineupEntry } from 'interfaces/team/ILineupEntry';
import { IPlayer } from 'interfaces/team/IPlayer';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useRef, useState } from 'react';
import { DragElementWrapper, DragSourceOptions } from 'react-dnd';
import StateManager from 'react-select';
import { FormatOptionLabelMeta } from 'react-select/lib/Select';
import { IFeatureConfigService } from 'services/FeatureConfigService';
import { useInjection } from 'services/ioc/ioc.react';
import { LineupStore } from 'stores/LineupStore';
import { NotificationStore } from 'stores/NotificationStore';
import { SessionStore } from 'stores/SessionStore';
import uuid from 'uuid';
import { NewAddPlaceholderPlayer } from '../../NewPlayerSelector/NewAddPlaceholderPlayer';
import { NewPlayerSelector } from '../../NewPlayerSelector/NewPlayerSelector';
import {
  ControlIcons,
  DNDIcon,
  Dot,
  EntryNumber,
  JerseyNumber,
  LineupPlayerWrapper,
  OptionLabel,
  PlayerWrapper,
  StyledDeleteIcon,
  StyledReactSelect,
} from './LineupPlayerStyles';

const MinPlayersForAmericanLeague = 10;
const DefaultDHPlayer = 1;

export interface IProps {
  playerSearchService: PlayerSearchService;
  unavailablePlayerIds: string[];
  lineupEntry: ILineupEntry;
  index: string;
  drag?: DragElementWrapper<DragSourceOptions>;
  filledPositions: string[];
  selectablePositions: string[];
  playerType?: PlayerType;
  hidePosition?: boolean;
  playerCount: number;
  playerLimit?: number;
  placeholderPlayerConfig?: IPlaceholderPlayerConfig;
  onChanged: (entry?: ILineupEntry) => void;
  lineup: ILineupEntry[];
  isScrimmage: boolean;
  containerClass?: string;
}

type SelectableOption = {
  value: string;
  label: string;
};

const mapPosition = (position?: string): SelectableOption => {
  if (!position) {
    return { value: '', label: '-' };
  }
  return { value: position, label: position };
};

export const LineupPlayer: React.FC<IProps> = observer((props: IProps) => {
  const featureConfigService = useInjection<IFeatureConfigService>(ServiceTypes.FeatureConfigService);
  const lineupStore = useInjection<LineupStore>(ServiceTypes.LineupStore);
  const sessionStore = useInjection<SessionStore>(ServiceTypes.SessionStore);
  const [alreadySelectedClass, setAlreadySelectedClass] = useState<string>('');
  const ref = useRef<StateManager>(null);
  const [playerRef, setPlayerRef] = useState<HTMLDivElement>();
  const {
    lineupEntry,
    unavailablePlayerIds,
    playerSearchService,
    filledPositions,
    index,
    drag,
    playerType,
    selectablePositions,
    hidePosition,
    onChanged,
    placeholderPlayerConfig,
    playerCount,
    playerLimit,
    lineup,
    isScrimmage,
    containerClass,
  } = props;

  const [isPitcher, setIsPitcher] = useState<boolean>(playerType === PlayerType.Pitcher);
  const [isDesignatedHitter, setIsDesignatedHitter] = useState<boolean>(playerType === PlayerType.DesignatedHitter);
  const [sameAsPitcher, setSameAsPitcher] = useState<boolean>(false);
  const [modalVisible, setModalVisible] = useState(false);
  const notificationStore = useInjection<NotificationStore>(ServiceTypes.NotificationStore);

  const checkIfPlayerIsSameAsPitcher = (): void => {
    const playerArray = lineup.filter((p: ILineupEntry) => p.player?.id === lineupEntry.player?.id);

    setSameAsPitcher(playerArray.length > 1);
  };

  const getSelectablePositions = (): SelectableOption[] => {
    if (isScrimmage && sameAsPitcher) {
      return [BaseballLineupPosition.DesignatedHitter, BaseballLineupPosition.Pitcher].map(mapPosition);
    }

    if (sameAsPitcher) {
      return [BaseballLineupPosition.DesignatedHitter].map(mapPosition);
    }

    return selectablePositions.map(mapPosition);
  };

  const focusSelect = (): void => {
    setTimeout(() => {
      if (ref.current !== null) {
        ref.current.select.focus();
      }
    }, 0.0001);
  };

  const onSelectionChange = (selected?: IPlayer): void => {
    if (selected) {
      onChanged({ ...lineupEntry, player: selected });
      checkIfPlayerIsSameAsPitcher();

      if (!isPitcher) {
        focusSelect();
      }
    } else {
      onChanged(undefined);
    }
  };

  const onJerseyNumberChange = (jerseyNumber: string) => {
    if (lineupEntry.player) {
      onChanged({
        ...lineupEntry,
        player: {
          ...lineupEntry.player,
          jerseyNumber: jerseyNumber,
        },
      });
    }
  };

  const validatePositionWithAdditionalDesignatedHitters = (): boolean => {
    if (playerLimit) {
      const additionalDesignatedHitterlimit = playerLimit - MinPlayersForAmericanLeague + DefaultDHPlayer;
      return (
        hidePosition ||
        !lineupEntry.position ||
        filledPositions.filter((p) => p === lineupEntry.position && lineupEntry.position === BaseballLineupPosition.DesignatedHitter)
          .length <= additionalDesignatedHitterlimit
      );
    }
    return true;
  };

  const validatePosition = (): boolean => {
    return (
      hidePosition ||
      !lineupEntry.position ||
      filledPositions.filter((p) => p === lineupEntry.position && p !== BaseballLineupPosition.DesignatedHitter).length < 2
    );
  };

  const setPositionAlreadySelected = (): void => {
    if (playerCount > MinPlayersForAmericanLeague) {
      if (validatePositionWithAdditionalDesignatedHitters()) {
        setAlreadySelectedClass('');
      } else {
        setAlreadySelectedClass(' red');
      }
    }
    if (validatePosition()) {
      setAlreadySelectedClass('');
    } else {
      setAlreadySelectedClass(' red');
    }
  };

  useEffect(() => {
    setPositionAlreadySelected();
    checkIfPlayerIsSameAsPitcher();
  }, [filledPositions, lineupEntry]);

  const isDragAvailable = () => {
    return !isPitcher || isScrimmage;
  };

  const renderOptionLabel = (
    option: { label: string; value: string },
    labelMeta: FormatOptionLabelMeta<{ label: string; value: string }>
  ) => {
    return (
      <OptionLabel>
        <div>{option.label}</div>
        {filledPositions.includes(option.label) && <SuccessBrandIcon />}
      </OptionLabel>
    );
  };

  const handleAddPlayerCallback = (
    firstName: string,
    lastName: string,
    jerseyNumber: string | undefined,
    foreignId: string,
    pitchingHandedness: Dexterity,
    battingHandedness: Dexterity
  ) => {
    const placeholderPlayer = {
      id: uuid(),
      firstName,
      lastName,
      positions: [],
      dexterity: { bats: battingHandedness, throws: pitchingHandedness },
      isPlaceholder: true,
      foreignIds: [foreignId],
      jerseyNumber,
      height: null,
      strikeZoneBottom: null,
      strikeZoneTop: null,
      birthDate: null,
      ids: [],
    } as IPlayer;
    onSelectionChange(placeholderPlayer);
    placeholderPlayerConfig!.onAddPlaceholderPlayer(placeholderPlayer);
    setModalVisible(false);
    notificationStore.addNotification({
      id: 'missing-player-added',
      message: 'Missing Player added to lineup',
      variant: 'warning',
      removable: true,
      onClose: () => {
        notificationStore.removeNotification('missing-player-added');
      },
    });
  };

  const newPlayerModal = (
    <NewAddPlaceholderPlayer
      onDismissCallback={() => {
        setModalVisible(false);
      }}
      onAddPlayerCallback={handleAddPlayerCallback}
      visible={modalVisible}
      allowAddingId={placeholderPlayerConfig?.allowEditingForeignId || false}
    />
  );

  const isStrikeZoneDataMissing = (): boolean => {
    if (!lineupEntry.player) {
      return false;
    }

    const activeStrikeZone =
      lineupStore.overridedStrikeZone?.activeStrikeZone ||
      sessionStore.currentSessionMetadata.strikeZone?.customDefinition?.activeStrikeZone ||
      sessionStore.currentSessionMetadata.strikeZone?.activeStrikeZone;

    switch (activeStrikeZone) {
      case StrikeZoneDefinitionEnum.HeightBased: {
        return !lineupEntry.player.height;
      }
      case StrikeZoneDefinitionEnum.TopAndBottomBased: {
        return !lineupEntry.player.strikeZoneBottom || !lineupEntry.player.strikeZoneTop;
      }
      case StrikeZoneDefinitionEnum.Static:
      default:
        return false;
    }
  };

  return (
    <LineupPlayerWrapper
      ref={(ref) => setPlayerRef(ref || undefined)}
      className={`lineup-player${!isDragAvailable() ? ' locked-pitcher' : ''}`}
      data-index={index}
      data-testid={`lineup-player-wrapper-${index}`}
    >
      <PlayerWrapper>
        {featureConfigService.getConfig().isRoboUmpEnabled && <Dot visible={isStrikeZoneDataMissing()}>SZ</Dot>}
        <EntryNumber>{index}</EntryNumber>
        {lineupEntry.player ? (
          <PlayerSelector
            index={Number.isNaN(parseInt(index)) ? '99' : index} // TODO: introduce proper fix for player suggestions overflow
            placeholder='Search for a player'
            unavailablePlayerIds={unavailablePlayerIds}
            playerSearchService={playerSearchService}
            onSelectionChange={onSelectionChange}
            placeholderPlayerConfig={placeholderPlayerConfig}
            selectedPlayer={lineupEntry.player}
            parentRef={playerRef}
            fixedNameWidth
            width='250px'
            handednessFields={[HandednessFields.BATTING, HandednessFields.PITCHING]}
            handednessOptions={Dexterity}
          />
        ) : (
          <NewPlayerSelector
            index={Number.isNaN(parseInt(index)) ? '99' : index}
            placeholder='Search for a player'
            unavailablePlayerIds={unavailablePlayerIds}
            playerSearchService={playerSearchService}
            onSelectionChange={onSelectionChange}
            modal={newPlayerModal}
            modalVisible={modalVisible}
            setModalVisible={setModalVisible}
            container={containerClass}
          />
        )}
      </PlayerWrapper>
      <ControlIcons className={`${lineupEntry.player || lineupEntry.isDesignatedHitter ? '' : 'greyed-out'}`}>
        <JerseyNumber
          maxLength={3}
          className='jersey-number'
          value={lineupEntry.player?.jerseyNumber}
          onChange={(e) => onJerseyNumberChange(e.target.value)}
          placeholder='#'
        />
        {!hidePosition && (
          <StyledReactSelect
            isDisabled={!lineupEntry.player || isPitcher || isDesignatedHitter}
            className={`position-select${alreadySelectedClass}`}
            classNamePrefix='position-select'
            value={mapPosition(lineupEntry.position)}
            placeholder='-'
            options={getSelectablePositions()}
            onChange={(newVal: any) =>
              onChanged({
                ...lineupEntry,
                position: newVal.value ? newVal.value : undefined,
              })
            }
            openMenuOnFocus
            menuPosition='fixed'
            ref={ref}
            menuPlacement={Number.parseInt(index) > 5 || index === 'F' || index === 'P' ? 'top' : 'auto'}
            formatOptionLabel={renderOptionLabel}
            data-testid={`player-${index}-position`}
          />
        )}
        <StyledDeleteIcon
          onClick={() => (lineupEntry.player || lineupEntry.isDesignatedHitter) && onSelectionChange(undefined)}
          data-testid={`lineup-remove-player-${index}`}
        />

        <DNDIcon ref={lineupEntry.player && isDragAvailable() ? drag : undefined} draggable={isDragAvailable()}>
          <DragDropIcon />
        </DNDIcon>
      </ControlIcons>
    </LineupPlayerWrapper>
  );
});
